import React, { useCallback, useEffect, useRef, useState } from 'react'
import { detectOS } from '@/src/renderer/lib/platform/detection'

const isMac = detectOS() === 'mac'

interface SavedInteractionState {
  selectedIds: Set<string>
  focusedIndex: number | null
  anchor: number | null
}

/** When provided, selection and keyboard navigation are managed by a parent hook. */
export interface ExternalSelectionProps {
  selectedIds: Set<string>
  focusedTaskId: string | null
  onSelect: (taskId: string, e: React.MouseEvent) => void
  onDoubleClick: (taskId: string) => void
  editingTaskId: string | null
  onCancelEdit: () => void
  onAutoSelect?: (taskId: string) => void
  onAutoCollapse?: (taskId: string) => void
}

interface UseTaskListInteractionOptions {
  onToggleComplete?: (ids: Set<string>) => void
  onDelete?: (ids: Set<string>) => void
  onOpen?: (taskId: string) => void
  onEdit?: (taskId: string) => void
  /** Called when ArrowUp is pressed on the first item (navigate out of list) */
  onEscapeTop?: () => void
  /** When provided, selection state is cached per key and restored on switch */
  stateKey?: string
  /** Task ID to auto-select once it appears in taskIds (e.g. after creation morph) */
  autoSelectId?: string | null
  /** Task ID to show expanded initially and then auto-collapse (e.g. after task creation) */
  autoCollapseId?: string | null
  /** When provided, parent manages selection/keyboard — this hook becomes passive. */
  externalSelection?: ExternalSelectionProps
}

/**
 * Reusable hook for task list selection, keyboard navigation, and inline editing.
 *
 * Handles:
 * - Click to select (single)
 * - Cmd/Ctrl+Click to toggle in/out of multi-selection
 * - Shift+Click for range selection
 * - Arrow Up/Down to navigate + select
 * - Shift+Arrow to extend selection range
 * - Space to toggle completion on selected items
 * - Enter to open focused item
 * - Escape to clear selection (or cancel editing)
 * - Click outside to clear selection
 * - Double-click to start inline editing
 *
 * @example
 * const { listProps, getItemProps, handleSelect, editingTaskId } = useTaskListInteraction(taskIds, {
 *   onToggleComplete: (ids) => toggleTasks(ids),
 *   onOpen: (id) => openTask(id),
 *   onEdit: (id) => console.log('editing', id),
 * })
 */
export function useTaskListInteraction(
  taskIds: string[],
  options: UseTaskListInteractionOptions = {},
) {
  const { onToggleComplete, onDelete, onOpen, onEdit, onEscapeTop, stateKey, autoSelectId, autoCollapseId, externalSelection } = options
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null)
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null)
  const anchorRef = useRef<number | null>(null)
  const listRef = useRef<HTMLDivElement>(null)

  // Per-key state cache — survives across stateKey switches
  const stateCache = useRef(new Map<string, SavedInteractionState>())
  const activeKeyRef = useRef(stateKey)
  const prevTaskIdsRef = useRef(taskIds)
  // When a task is deleted, store the ID of the task that should receive focus.
  // The correction logic below resolves the correct index once taskIds updates.
  const pendingFocusIdRef = useRef<string | null>(null)

  // Save/restore selection state when stateKey changes
  if (activeKeyRef.current !== stateKey) {
    // Save outgoing state
    if (activeKeyRef.current !== undefined) {
      stateCache.current.set(activeKeyRef.current, {
        selectedIds,
        focusedIndex,
        anchor: anchorRef.current,
      })
    }
    activeKeyRef.current = stateKey

    // Restore incoming state
    const saved = stateKey ? stateCache.current.get(stateKey) : undefined
    if (saved) {
      setSelectedIds(saved.selectedIds)
      setFocusedIndex(saved.focusedIndex)
      anchorRef.current = saved.anchor
    } else {
      setSelectedIds(new Set())
      setFocusedIndex(null)
      anchorRef.current = null
    }
    setEditingTaskId(null)
    prevTaskIdsRef.current = taskIds
  }

  // Correct focusedIndex after taskIds change (reorder, deletion, etc.).
  if (prevTaskIdsRef.current !== taskIds) {
    if (pendingFocusIdRef.current) {
      // A deletion requested focus on a specific task — resolve its index now
      const targetId = pendingFocusIdRef.current
      pendingFocusIdRef.current = null
      const newIdx = taskIds.indexOf(targetId)
      if (newIdx !== -1) {
        setFocusedIndex(newIdx)
        anchorRef.current = newIdx
      } else {
        setFocusedIndex(null)
        anchorRef.current = null
      }
    } else if (focusedIndex !== null) {
      // Reorder case: the focused item may have moved to a new index
      const prevFocusedId = prevTaskIdsRef.current[focusedIndex]
      if (prevFocusedId) {
        const newIdx = taskIds.indexOf(prevFocusedId)
        if (newIdx !== -1 && newIdx !== focusedIndex) {
          setFocusedIndex(newIdx)
          anchorRef.current = newIdx
        } else if (newIdx === -1) {
          setFocusedIndex(null)
          anchorRef.current = null
        }
      }
    }
  }
  prevTaskIdsRef.current = taskIds

  const selectRange = useCallback((from: number, to: number) => {
    const start = Math.min(from, to)
    const end = Math.max(from, to)
    return new Set(taskIds.slice(start, end + 1))
  }, [taskIds])

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set())
    setFocusedIndex(null)
    anchorRef.current = null
  }, [])

  const handleSelect = useCallback((taskId: string, e: React.MouseEvent) => {
    // Controlled mode: delegate to parent
    if (externalSelection) {
      externalSelection.onSelect(taskId, e)
      return
    }
    // Cancel editing and proceed with selection (click-outside already submitted via pointerdown)
    if (editingTaskId) {
      setEditingTaskId(null)
    }

    const idx = taskIds.indexOf(taskId)
    if (idx === -1) return

    if (e.shiftKey && anchorRef.current !== null) {
      const range = selectRange(anchorRef.current, idx)
      setSelectedIds(prev => {
        if (e.metaKey || e.ctrlKey) {
          const next = new Set(prev)
          range.forEach(id => next.add(id))
          return next
        }
        return range
      })
      setFocusedIndex(idx)
      listRef.current?.focus({ preventScroll: true })
      return
    }

    if (e.metaKey || e.ctrlKey) {
      setSelectedIds(prev => {
        const next = new Set(prev)
        if (next.has(taskId)) next.delete(taskId)
        else next.add(taskId)
        return next
      })
      // Keep focus only if the task stays selected
      if (selectedIds.has(taskId)) {
        // Task is being deselected — move focus to last remaining selected item
        const remaining = [...selectedIds].filter(id => id !== taskId)
        if (remaining.length > 0) {
          const lastId = remaining[remaining.length - 1]
          setFocusedIndex(taskIds.indexOf(lastId))
        } else {
          setFocusedIndex(null)
        }
      } else {
        setFocusedIndex(idx)
      }
    } else {
      setSelectedIds(new Set([taskId]))
      setFocusedIndex(idx)
    }

    anchorRef.current = idx

    // Ensure the list div has DOM focus so subsequent keyboard events
    // (e.g. ArrowUp → onEscapeTop) fire on the list's onKeyDown handler.
    listRef.current?.focus({ preventScroll: true })
  }, [taskIds, selectRange, editingTaskId, externalSelection])

  // Double-click → start editing (keep task selected so it stays selected after closing)
  const handleDoubleClick = useCallback((taskId: string) => {
    if (externalSelection) {
      externalSelection.onDoubleClick(taskId)
      return
    }
    const idx = taskIds.indexOf(taskId)
    setSelectedIds(new Set([taskId]))
    setFocusedIndex(idx)
    anchorRef.current = idx
    setEditingTaskId(taskId)
    onEdit?.(taskId)
  }, [taskIds, onEdit, externalSelection])

  // Cancel editing → return to collapsed mode and refocus list for keyboard nav
  const cancelEditing = useCallback(() => {
    if (externalSelection) {
      externalSelection.onCancelEdit()
      return
    }
    setEditingTaskId(null)
    listRef.current?.focus({ preventScroll: true })
  }, [externalSelection])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Controlled mode: parent handles keyboard
    if (externalSelection) return
    // Don't handle keyboard when editing (TaskEntry handles its own keys)
    if (editingTaskId) return

    const len = taskIds.length
    if (len === 0) return

    // Jump to first/last item (Home/End on Win/Linux, Cmd+Arrow on macOS)
    const isJumpToStart = isMac ? (e.metaKey && e.key === 'ArrowUp') : e.key === 'Home'
    const isJumpToEnd = isMac ? (e.metaKey && e.key === 'ArrowDown') : e.key === 'End'

    if (isJumpToStart || isJumpToEnd) {
      e.preventDefault()
      const targetIdx = isJumpToStart ? 0 : len - 1

      setFocusedIndex(targetIdx)

      if (e.shiftKey) {
        if (anchorRef.current === null) anchorRef.current = focusedIndex ?? 0
        setSelectedIds(selectRange(anchorRef.current, targetIdx))
      } else {
        anchorRef.current = targetIdx
        setSelectedIds(new Set([taskIds[targetIdx]]))
      }

      // Scroll the page fully to top/bottom so the item isn't hidden behind the gradient.
      // Deferred via setTimeout so it runs AFTER React's useEffect (scrollIntoView in
      // TaskItem/TaskEntry) which would otherwise override this scroll.
      const jumpToStart = isJumpToStart
      setTimeout(() => {
        const scrollContainer = listRef.current?.closest('.main-layout__scroll-wrap') as HTMLElement | null
        if (scrollContainer) {
          scrollContainer.scrollTo({ top: jumpToStart ? 0 : scrollContainer.scrollHeight, behavior: 'smooth' })
        }
      }, 0)
      return
    }

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      const dir = e.key === 'ArrowDown' ? 1 : -1

      // ArrowUp on first selected item → escape to header (or clamp)
      if (e.key === 'ArrowUp' && focusedIndex === 0 && !e.shiftKey) {
        if (onEscapeTop) {
          clearSelection()
          onEscapeTop()
          return
        }
        // No escape target — stay at first item
        return
      }

      let nextIdx: number
      if (focusedIndex === null) {
        // No selection: ArrowDown → first, ArrowUp → last
        nextIdx = e.key === 'ArrowDown' ? 0 : len - 1
      } else {
        nextIdx = Math.max(0, Math.min(len - 1, focusedIndex + dir))
      }

      setFocusedIndex(nextIdx)

      if (e.shiftKey) {
        if (anchorRef.current === null) anchorRef.current = focusedIndex ?? 0
        setSelectedIds(selectRange(anchorRef.current, nextIdx))
      } else {
        anchorRef.current = nextIdx
        setSelectedIds(new Set([taskIds[nextIdx]]))
      }
      return
    }

    if ((e.metaKey || e.ctrlKey) && e.key === 'a') {
      e.preventDefault()
      setSelectedIds(new Set(taskIds))
      setFocusedIndex(len - 1)
      anchorRef.current = 0
      return
    }

    if (e.code === 'Space' && selectedIds.size > 0) {
      e.preventDefault()
      onToggleComplete?.(new Set(selectedIds))
      return
    }

    if (e.key === 'Enter' && focusedIndex !== null) {
      e.preventDefault()
      handleDoubleClick(taskIds[focusedIndex])
      return
    }

    if ((e.key === 'Backspace' || e.key === 'Delete') && selectedIds.size > 0 && onDelete) {
      e.preventDefault()
      const idsToDelete = new Set(selectedIds)
      const remaining = taskIds.filter(id => !idsToDelete.has(id))

      if (remaining.length === 0) {
        clearSelection()
      } else {
        // Find first non-deleted task after the last selected one
        const lastSelectedIdx = Math.max(...[...idsToDelete].map(id => taskIds.indexOf(id)))
        const nextAfter = taskIds.slice(lastSelectedIdx + 1).find(id => !idsToDelete.has(id))
        const selectId = nextAfter ?? remaining[remaining.length - 1]
        setSelectedIds(new Set([selectId]))
        // Don't set focusedIndex here — taskIds hasn't updated yet.
        // Store the target ID and let the correction logic resolve the
        // correct index once React re-renders with the new taskIds.
        pendingFocusIdRef.current = selectId
      }

      onDelete(idsToDelete)
      requestAnimationFrame(() => listRef.current?.focus({ preventScroll: true }))
      return
    }

    if (e.key === 'Escape') {
      e.preventDefault()
      clearSelection()
      return
    }
  }, [taskIds, focusedIndex, selectedIds, selectRange, clearSelection, onToggleComplete, onDelete, onOpen, onEscapeTop, editingTaskId, externalSelection])

  // Clear editingTaskId when the edited task is removed from the list
  useEffect(() => {
    if (editingTaskId && !taskIds.includes(editingTaskId)) {
      setEditingTaskId(null)
    }
  }, [taskIds, editingTaskId])

  // Global arrow keys → focus list and select first/last when nothing is selected
  useEffect(() => {
    if (externalSelection) return // parent manages keys
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return
      if (editingTaskId) return
      if (taskIds.length === 0) return

      const tag = (e.target as HTMLElement)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      // Don't intercept keys when a dropdown/context menu is open
      if (document.querySelector('[role="menu"]')) return

      // Cmd/Ctrl+A → select all (works even without prior selection)
      if ((e.metaKey || e.ctrlKey) && e.key === 'a') {
        e.preventDefault()
        setSelectedIds(new Set(taskIds))
        setFocusedIndex(taskIds.length - 1)
        anchorRef.current = 0
        listRef.current?.focus({ preventScroll: true })
        return
      }

      if (selectedIds.size > 0 || focusedIndex !== null) return
      if (e.ctrlKey || e.metaKey || e.altKey) return

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        const idx = e.key === 'ArrowDown' ? 0 : taskIds.length - 1
        setFocusedIndex(idx)
        anchorRef.current = idx
        setSelectedIds(new Set([taskIds[idx]]))
        listRef.current?.focus({ preventScroll: true })
      }
    }
    window.addEventListener('keydown', handleGlobalKeyDown)
    return () => window.removeEventListener('keydown', handleGlobalKeyDown)
  }, [taskIds, selectedIds, focusedIndex, editingTaskId, externalSelection])

  // Click outside → clear selection (ignore clicks on section headers — they manage selection)
  useEffect(() => {
    if (externalSelection) return // parent manages click-outside
    const handleClickOutside = (e: MouseEvent) => {
      if (listRef.current && !listRef.current.contains(e.target as Node)) {
        if ((e.target as HTMLElement).closest('.section-header')) return
        // Don't clear when clicking the dropdown backdrop overlay or an open dropdown
        if ((e.target as HTMLElement).closest('[data-task-dropdown-backdrop], [data-task-dropdown]')) return
        clearSelection()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [clearSelection, externalSelection])

  const selectTask = useCallback((taskId: string) => {
    const idx = taskIds.indexOf(taskId)
    if (idx === -1) return
    setSelectedIds(new Set([taskId]))
    setFocusedIndex(idx)
    anchorRef.current = idx
    listRef.current?.focus({ preventScroll: true })
  }, [taskIds])

  const selectTasks = useCallback((ids: string[]) => {
    if (ids.length === 0) return
    setSelectedIds(new Set(ids))
    const firstIdx = taskIds.indexOf(ids[0])
    setFocusedIndex(firstIdx >= 0 ? firstIdx : null)
    anchorRef.current = firstIdx >= 0 ? firstIdx : null
    listRef.current?.focus({ preventScroll: true })
  }, [taskIds])

  const focusFirst = useCallback(() => {
    if (taskIds.length === 0) return
    setSelectedIds(new Set([taskIds[0]]))
    setFocusedIndex(0)
    anchorRef.current = 0
    listRef.current?.focus({ preventScroll: true })
  }, [taskIds])

  // Focus the list wrapper without changing selection. Used when a tab
  // becomes active so keyboard nav works without forcing a new selection.
  const focusList = useCallback(() => {
    listRef.current?.focus({ preventScroll: true })
  }, [])

  // Auto-select a task once it appears in taskIds (e.g. after creation morph)
  const didAutoSelectRef = useRef<string | null>(null)
  useEffect(() => {
    if (!autoSelectId || autoSelectId === didAutoSelectRef.current) return
    const idx = taskIds.indexOf(autoSelectId)
    if (idx === -1) return
    didAutoSelectRef.current = autoSelectId
    if (externalSelection) {
      externalSelection.onAutoSelect?.(autoSelectId)
    } else {
      setSelectedIds(new Set([autoSelectId]))
      setFocusedIndex(idx)
      anchorRef.current = idx
    }
  }, [taskIds, autoSelectId, externalSelection])

  // Auto-collapse: show task expanded initially, then collapse after one frame.
  // Uses synchronous setState-during-render for LOCAL state (same component) so the
  // task renders expanded on the FIRST paint — no flash of collapsed state.
  // External (parent) state updates go through a microtask to avoid the React warning
  // "Cannot update a component while rendering a different component".
  const didAutoCollapseRef = useRef<string | null>(null)
  const isAutoCollapsingRef = useRef(false)
  const pendingAutoCollapseRef = useRef<string | null>(null)
  if (autoCollapseId && autoCollapseId !== didAutoCollapseRef.current && taskIds.includes(autoCollapseId)) {
    didAutoCollapseRef.current = autoCollapseId
    isAutoCollapsingRef.current = true
    if (externalSelection) {
      // Defer parent state update to avoid setState-during-render of a different component
      pendingAutoCollapseRef.current = autoCollapseId
    } else {
      const idx = taskIds.indexOf(autoCollapseId)
      setEditingTaskId(autoCollapseId)
      setSelectedIds(new Set([autoCollapseId]))
      setFocusedIndex(idx)
      anchorRef.current = idx
    }
  }

  // Flush deferred onAutoCollapse to parent after render
  useEffect(() => {
    const taskId = pendingAutoCollapseRef.current
    if (!taskId) return
    pendingAutoCollapseRef.current = null
    externalSelection?.onAutoCollapse?.(taskId)
  })

  // Schedule collapse after the expanded render is painted
  const effectiveEditingForCollapse = externalSelection?.editingTaskId ?? editingTaskId
  useEffect(() => {
    if (!effectiveEditingForCollapse || !isAutoCollapsingRef.current) return
    const id = requestAnimationFrame(() => {
      isAutoCollapsingRef.current = false
      if (externalSelection) {
        externalSelection.onCancelEdit?.()
      } else {
        setEditingTaskId(null)
        listRef.current?.focus({ preventScroll: true })
      }
    })
    return () => cancelAnimationFrame(id)
  }, [effectiveEditingForCollapse, externalSelection])

  // Auto-select the first item when the list receives focus via Tab navigation.
  // Other focus sources (mouse click, programmatic .focus()) already handle
  // selection in their own handlers. We detect Tab by tracking whether the Tab
  // key is currently held down (capture-phase listeners fire before focus).
  const tabActiveRef = useRef(false)

  useEffect(() => {
    const onDown = (e: KeyboardEvent) => { if (e.key === 'Tab') tabActiveRef.current = true }
    const onUp = (e: KeyboardEvent) => { if (e.key === 'Tab') tabActiveRef.current = false }
    document.addEventListener('keydown', onDown, true)
    document.addEventListener('keyup', onUp, true)
    return () => {
      document.removeEventListener('keydown', onDown, true)
      document.removeEventListener('keyup', onUp, true)
    }
  }, [])

  const handleFocus = useCallback(() => {
    if (!tabActiveRef.current) return
    if (editingTaskId) return
    if (selectedIds.size > 0 || focusedIndex !== null) return
    if (taskIds.length === 0) return
    setSelectedIds(new Set([taskIds[0]]))
    setFocusedIndex(0)
    anchorRef.current = 0
  }, [taskIds, selectedIds, focusedIndex, editingTaskId])

  const listProps = {
    ref: listRef,
    tabIndex: 0,
    onKeyDown: handleKeyDown,
    onFocus: handleFocus,
  }

  const getItemProps = useCallback((index: number) => {
    const taskId = taskIds[index]
    // Immediately mark autoSelectId as selected so the first render already
    // shows the selection style — avoids a white flash before the useEffect fires.
    const isAutoSelected = !!autoSelectId && taskId === autoSelectId
    if (externalSelection) {
      return {
        selected: externalSelection.selectedIds.has(taskId) || isAutoSelected,
        focused: externalSelection.focusedTaskId === taskId && externalSelection.selectedIds.has(taskId),
      }
    }
    return {
      selected: selectedIds.has(taskId) || isAutoSelected,
      focused: focusedIndex === index && selectedIds.has(taskId),
    }
  }, [selectedIds, focusedIndex, taskIds, externalSelection, autoSelectId])

  // Expose which task (if any) is currently being auto-collapsed.
  // Computed from the ref so it's only truthy during the one-frame auto-collapse render.
  const autoCollapsingTaskId = isAutoCollapsingRef.current ? autoCollapseId : null

  const effectiveEditingTaskId = externalSelection?.editingTaskId ?? editingTaskId

  return {
    selectedIds,
    focusedIndex,
    editingTaskId: effectiveEditingTaskId,
    autoCollapsingTaskId,
    listProps,
    getItemProps,
    handleSelect,
    handleDoubleClick,
    clearSelection,
    selectTask,
    selectTasks,
    focusFirst,
    focusList,
    cancelEditing,
  }
}
